"use client"

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cn } from "~/lib/utils"
import { cva, VariantProps } from "class-variance-authority"

const DropdownMenu = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode
    className?: string
    [key: string]: any
  }
>(({ children, className, ...props }, ref) => {
  return (
    <div ref={ref} className={cn("dropdown", className)} {...props}>
      {children}
    </div>
  )
})
DropdownMenu.displayName = "DropdownMenu"

const DropdownMenuTrigger = ({
  children,
  className,
  asChild = false,
  customPosition,
  ...props
}: {
  children: React.ReactNode
  className?: string
  asChild?: boolean
  customPosition?: (triggerElement: HTMLElement) => void
  [key: string]: any
}) => {
  const Comp = asChild ? Slot : "button"
  const triggerRef = React.useRef<HTMLButtonElement>(null)

  React.useEffect(() => {
    if (!customPosition || !triggerRef.current) return

    const triggerElement = triggerRef.current as HTMLElement

    const handleShow = () => {
      requestAnimationFrame(() => {
        customPosition(triggerElement)
      })
    }

    triggerElement.addEventListener("show.bs.dropdown", handleShow)

    return () => {
      triggerElement.removeEventListener("show.bs.dropdown", handleShow)
    }
  }, [customPosition])

  return (
    <Comp
      ref={triggerRef}
      className={cn(className)}
      data-bs-auto-close="outside"
      data-bs-toggle="dropdown"
      title=""
      type="button"
      value=""
      {...props}
    >
      {children}
    </Comp>
  )
}
DropdownMenuTrigger.displayName = "DropdownMenuTrigger"

const dropdownMenuContentVariants = cva("dropdown-menu", {
  variants: {
    size: {
      md: "dropdown-menu--md",
    },
  },
})

const dropdownMenuItemVariants = cva("dropdown-item", {
  variants: {
    clickable: {
      true: "dropdown-item--clickable",
    },
    small: {
      true: "dropdown-item--small",
    },
  },
  defaultVariants: {
    clickable: true,
  },
})

const dropdownMenuTitleVariants = cva("dropdown-menu__title", {
  variants: {
    small: {
      true: "dropdown-menu__title--small",
    },
  },
})

const DropdownMenuContent = ({
  children,
  className,
  asChild = false,
  size,
  ...props
}: {
  children: React.ReactNode
  className?: string
  asChild?: boolean
} & VariantProps<typeof dropdownMenuContentVariants>) => {
  const Comp = asChild ? Slot : "ul"
  return (
    <Comp className={cn(dropdownMenuContentVariants({ size, className }))} {...props}>
      {children}
    </Comp>
  )
}
DropdownMenuContent.displayName = "DropdownMenuContent"

const DropdownBody = ({
  children,
  className,
  ...props
}: {
  children: React.ReactNode
  className?: string
  [key: string]: any
}) => {
  return (
    <div className={cn("dropdown__body", className)} {...props}>
      {children}
    </div>
  )
}
DropdownBody.displayName = "DropdownBody"

const DropdownFooter = ({
  children,
  className,
  ...props
}: {
  children: React.ReactNode
  className?: string
  [key: string]: any
}) => {
  return (
    <footer className={cn("dropdown__footer", className)} {...props}>
      {children}
    </footer>
  )
}
DropdownFooter.displayName = "DropdownFooter"

const DropdownMenuItem = ({
  children,
  className,
  asChild = false,
  closeOnClick = false,
  small = false,
  ...props
}: {
  children: React.ReactNode
  className?: string
  asChild?: boolean
  closeOnClick?: boolean
  small?: boolean
  [key: string]: any
}) => {
  const Comp = asChild ? Slot : "li"

  const handleClick = (event: React.MouseEvent) => {
    if (closeOnClick) {
      const dropdownElement = (event.currentTarget as HTMLElement).closest(".dropdown")
      if (dropdownElement) {
        const dropdown = (window as any).bootstrap?.Dropdown.getInstance(
          dropdownElement.querySelector('[data-bs-toggle="dropdown"]'),
        )
        dropdown?.hide()
      }
    }

    if (props.onClick) {
      props.onClick(event)
    }
  }

  return (
    <Comp className={cn(dropdownMenuItemVariants({ small }), className)} {...props} onClick={handleClick}>
      {children}
    </Comp>
  )
}
DropdownMenuItem.displayName = "DropdownMenuItem"

const DropdownMenuSubmit = ({
  children,
  className,
  ...props
}: {
  children: React.ReactNode
  className?: string
  [key: string]: any
}) => {
  return (
    <button className={cn("dropdown-menu__submit", className)} {...props}>
      {children}
    </button>
  )
}
DropdownMenuSubmit.displayName = "DropdownMenuSubmit"

const DropdownMenuHeader = ({
  children,
  className,
  ...props
}: {
  children: React.ReactNode
  className?: string
  [key: string]: any
}) => {
  return (
    <div className={cn("dropdown-menu__header", className)} {...props}>
      {children}
    </div>
  )
}
DropdownMenuHeader.displayName = "DropdownMenuHeader"

const DropdownMenuTitle = ({
  children,
  className,
  small = false,
  ...props
}: {
  children: React.ReactNode
  className?: string
  small?: boolean
  [key: string]: any
}) => {
  return (
    <div className={cn(dropdownMenuTitleVariants({ small }), className)} {...props}>
      {children}
    </div>
  )
}
DropdownMenuTitle.displayName = "DropdownMenuTitle"

const DropdownMenuInput = ({
  children,
  className,
  ...props
}: {
  children: React.ReactNode
  className?: string
  [key: string]: any
}) => {
  return (
    <div className={cn("dropdown-item__input", className)} {...props}>
      {children}
    </div>
  )
}
DropdownMenuInput.displayName = "DropdownMenuInput"

const DropdownMenuAction = ({
  children,
  className,
  variant = "default",
  ...props
}: {
  children: React.ReactNode
  className?: string
  variant?: "default" | "destructive"
  [key: string]: any
}) => {
  return (
    <button
      className={cn("dropdown-menu__action", variant === "destructive" && "dropdown-menu__action--danger", className)}
      {...props}
    >
      {children}
    </button>
  )
}
DropdownMenuAction.displayName = "DropdownMenuAction"

const DropdownMenuDivider = ({ className, ...props }: { className?: string; [key: string]: any }) => {
  return <hr className={cn("dropdown-divider", className)} {...props} />
}
DropdownMenuDivider.displayName = "DropdownMenuDivider"

const DropdownMenuHint = ({
  children,
  className,
  ...props
}: {
  children: React.ReactNode
  className?: string
  [key: string]: any
}) => {
  return (
    <div className={cn("dropdown-menu-hint", className)} {...props}>
      {children}
    </div>
  )
}
DropdownMenuHint.displayName = "DropdownMenuHint"

const DropdownSearchInput = React.forwardRef<
  HTMLInputElement,
  {
    className?: string
    [key: string]: any
  }
>(({ className, ...props }, ref) => {
  return <input ref={ref} type="search" className={cn("dropdown-search__input", className)} {...props} />
})
DropdownSearchInput.displayName = "DropdownSearchInput"

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownBody,
  DropdownMenuHeader,
  DropdownMenuTitle,
  DropdownFooter,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSubmit,
  DropdownMenuInput,
  DropdownMenuAction,
  DropdownMenuDivider,
  DropdownMenuHint,
  DropdownSearchInput,
}
